/**
 * The Gramas mark, as numbers.
 *
 * It is the day's energy ring tilted until it reads as a G: the same circle,
 * the same constant width, with the gap slid round to where a letter's
 * aperture belongs and the gauge needle serving as the crossbar. Because the
 * width never varies, the gap is a dash — which is the only reason the mark
 * can start a launch as an upright gauge, turn into the letter, and land back
 * on Today's ring. A modulated stroke would have to be a filled shape, and a
 * filled shape has nothing to slide.
 *
 * `apps/web/assets/brand/mark.svg` carries the same two paths and is the input
 * to `apps/web/scripts/generate-assets.mjs`; a test asserts the two agree.
 */
export const MARK = {
  /** viewBox side. Everything below is in these units. */
  box: 512,
  /** Radius of the bowl's centre line, and its stroke width. */
  R: 144,
  w: 60,
  /** Degrees. Applied about the centre; the letter is never drawn upright. */
  tilt: -20,
  /** Circumference of the bowl, declared as `pathLength` so the dash figures
   *  below are exact rather than subject to the engine's arc-length estimate. */
  circ: 904.78,
  /** Length of the crossbar, from the rim inwards. */
  barLen: 107.72,

  /** The still letter: an open bowl … */
  bowl: 'M 363.01 159.65 A 144 144 0 1 0 395.72 290.84',
  /** … and the crossbar, which starts on the rim and runs in. */
  bar: 'M 395.72 290.84 L 288 290.84',
  /**
   * The whole circle, drawn *clockwise* from the crossbar terminal, so that
   * distance along the path maps straight onto clock angle — and so that the
   * ring fills the same way Today's does, from twelve o'clock round.
   */
  ring: 'M 395.72 290.84 A 144 144 0 1 1 116.28 221.16 A 144 144 0 1 1 395.72 290.84',

  /** Gap at the upper right, 56°: the letter. */
  letter: { dash: 764.04, gap: 140.74, offset: 0 },
  /** Gap astride twelve o'clock, 72°: an ordinary gauge. */
  gauge: { dash: 723.82, gap: 180.96, offset: 170.9 },
  /** Dash offset that starts an arc of any length at twelve o'clock. */
  dayOffset: 261.38,

  /**
   * A gap the size of the whole path would leave a zero-length dash, and a
   * zero-length dash with a round cap is painted as a dot — which is how an
   * undrawn crossbar ends up as a bead floating in the counter. Keeping the
   * gap longer than the path moves that dot off the end of it.
   */
  barDash: '107.72 430.88',
} as const

/** The mark's box, as a multiple of the word's font size, in the lockup. */
export const LOCKUP_RATIO = 1.36
